import { PaloColumnVariant } from './palo-column-variant';

/**
 * What a column holds. The width and the alignment follow from it.
 *
 * Until now every column carried its own width, mostly as a percentage: 284
 * of 484 columns did, which is why the same column was a different width in
 * every list and at every window size. A column now says what kind of value
 * it shows, and the kind carries the measurements (LST-S1, LST-S1a and the
 * decisions of 12.10.2026 in LST-S17).
 *
 * A list may still set its own width where it has a reason — the reason
 * belongs in a comment at that column.
 */
export type PaloColumnType =
    | 'status'
    | 'deadline'
    | 'documentNumber'
    | 'articleNumber'
    | 'date'
    | 'deliveryDate'
    | 'timestamp'
    | 'amount'
    | 'price'
    | 'quantity'
    | 'percent'
    | 'count'
    | 'flag'
    | 'select'
    | 'phone'
    | 'name'
    | 'classification'
    | 'rating'
    | 'progress'
    | 'fileSize'
    | 'fileType'
    | 'icon';

export interface PaloColumnTypeSpec {
    /** Fixed width; the name column is the one that takes what is left over. */
    width?: number;
    minWidth?: number;
    align?: 'left' | 'center' | 'right';
    variant?: PaloColumnVariant;
}

export const PALO_COLUMN_TYPES: Record<PaloColumnType, PaloColumnTypeSpec> = {
    /** 180 px, one line, never shortened: "Beendet ohne Folgebeleg" fits. */
    status: { width: 180, align: 'left' },
    /** Right of the status: "+925 T." when a document has run past its date. */
    deadline: { width: 100, align: 'left' },
    /**
     * Document, customer and supplier numbers. Sized for the longest one
     * beside its note mark: "AR26-DM-01082" in Inter is 103 px, the mark
     * 22 px, the padding 20 px (#2318).
     */
    documentNumber: { width: 145, align: 'left', variant: 'ref' },
    /**
     * Article and offer numbers are their own kind: the longest in the
     * catalogue is 41 characters, and 1.020 articles carry 35 or more.
     */
    articleNumber: { width: 160, align: 'left', variant: 'ref' },
    date: { width: 120, align: 'left', variant: 'date' },
    /** A date with its kind before it — "bis 02. Okt. 2026" (#2267). */
    deliveryDate: { width: 160, align: 'left', variant: 'date' },
    /** A moment, not a day — the history and the audit trail. */
    timestamp: { width: 150, align: 'left', variant: 'date' },
    /** 14 characters of digits; the € stands in the heading, not in the cell. */
    amount: { width: 150, align: 'right', variant: 'amount' },
    price: { width: 150, align: 'right', variant: 'amount' },
    /** Number and unit side by side, up to 20 characters together (LST-S1a). */
    quantity: { width: 200, align: 'right', variant: 'amount' },
    percent: { width: 80, align: 'right', variant: 'amount' },
    count: { width: 80, align: 'right', variant: 'amount' },
    flag: { width: 80, align: 'center' },
    /** A box to tick, and nothing else — no heading over it (LST-S17). */
    select: { width: 40, align: 'center' },
    /** 22 characters: 86 % of the numbers on file are longer than 16. */
    phone: { width: 170, align: 'left' },
    /** Takes the width the fixed columns leave over. */
    name: { minWidth: 240, align: 'left' },
    classification: { width: 150, align: 'left' },
    rating: { width: 100, align: 'right' },
    progress: { width: 120, align: 'left' },
    fileSize: { width: 80, align: 'right', variant: 'amount' },
    fileType: { width: 40, align: 'center' },
    /** A mark or a single action, without a heading. */
    icon: { width: 60, align: 'center' },
};
